/**
 * EditorLayout Component for UI-Model-1
 *
 * Main editor shell containing:
 * - EditorHeader (top bar)
 * - VerticalNav (left sidebar tabs: Layout/CSS/Theme)
 * - Main content area (LayoutView/CssEditor/ThemeEditor based on active tab)
 *
 * Layout Structure:
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │                           EDITOR HEADER                                   │
 * │  ← Back   [Logo] Model Name ✏️                              [Preview]     │
 * ├────────┬─────────────────────────────────────────────────────────────────┤
 * │        │                                                                  │
 * │  V     │  Content based on active tab:                                   │
 * │  E     │  - Layout: LayoutView (header preview + nav + canvas panels)    │
 * │  R     │  - CSS: CssEditor                                               │
 * │  T     │  - Theme: ThemeEditor                                           │
 * │  I     │                                                                  │
 * │  C     │                                                                  │
 * │  A     │                                                                  │
 * │  L     │                                                                  │
 * │        │                                                                  │
 * │  T     │                                                                  │
 * │  A     │                                                                  │
 * │  B     │                                                                  │
 * │  S     │                                                                  │
 * │        │                                                                  │
 * └────────┴─────────────────────────────────────────────────────────────────┘
 */

import React from 'react';
import { useVerticalNav, VerticalNavProvider } from '../../contexts/VerticalNavContext';
import { useUIModelState } from '../../contexts/UIModelStateContext';
import EditorHeader from '../EditorHeader';
import VerticalNav from '../VerticalNav';
import LayoutView from '../LayoutView';
import CssEditor from '../CssEditor';
import ThemeEditor from '../ThemeEditor';
import SaveStatusIndicator from '../SaveStatusIndicator';

interface EditorLayoutProps {
  id?: string;
  projectId: string;
  onBackClick: () => void;
  onEditClick: () => void;
  onPreviewClick: () => void;
  hasEditPermission?: boolean;
}

/**
 * Inner component that uses the VerticalNav context
 */
const EditorLayoutInner = ({
  id,
  projectId,
  onBackClick,
  onEditClick,
  onPreviewClick,
  hasEditPermission,
}: EditorLayoutProps) => {
  const baseId = id || 'editor-layout';
  const { activeTab } = useVerticalNav();
  const { state } = useUIModelState();

  const renderContent = () => {
    switch (activeTab) {
      case 'layout':
        return <LayoutView id={`${baseId}-layout-view`} />;
      case 'css':
        return <CssEditor id={`${baseId}-css-editor`} projectId={projectId} />;
      case 'theme':
        return <ThemeEditor id={`${baseId}-theme-editor`} projectId={projectId} />;
      default:
        return <LayoutView id={`${baseId}-layout-view`} />;
    }
  };

  return (
    <div
      id={baseId}
      data-testid={baseId}
      className="tw-flex tw-flex-col tw-h-screen tw-bg-surface-sunken"
    >
      {/* Editor Header */}
      <EditorHeader
        id={`${baseId}-header`}
        uiModel={state.model}
        onBackClick={onBackClick}
        onEditClick={onEditClick}
        onPreviewClick={onPreviewClick}
        hasEditPermission={hasEditPermission}
      />

      {/* Main Content Area */}
      <div className="tw-flex tw-flex-1 tw-overflow-hidden">
        {/* Vertical Navigation Tabs */}
        <VerticalNav id={`${baseId}-vertical-nav`} />

        {/* Tab Content */}
        <div className="tw-flex-1 tw-overflow-hidden">{renderContent()}</div>
      </div>

      {/* Auto-save status indicator (bottom-right toast) */}
      <SaveStatusIndicator />
    </div>
  );
};

/**
 * EditorLayout wraps the inner component with VerticalNavProvider
 */
export const EditorLayout = (props: EditorLayoutProps) => {
  return (
    <VerticalNavProvider>
      <EditorLayoutInner {...props} />
    </VerticalNavProvider>
  );
};

export default EditorLayout;
